import React from "react";

const GREEN = "#02BE0F";
const DARK = "#102417";
const LIGHT_GREEN = "#EFFFF1";

const pageStyle = {
  width: "100%",
  overflow: "hidden",
  background:
    "linear-gradient(180deg, #f7fff8 0%, #ffffff 35%, #f7fff8 100%)",
  color: DARK,
  fontFamily:
    "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const containerStyle = {
  width: "min(1180px, calc(100% - 36px))",
  margin: "0 auto",
};

const sectionStyle = {
  padding: "80px 0",
};

const glassCard = {
  background: "rgba(255,255,255,0.92)",
  border: "1px solid rgba(2,190,15,0.12)",
  borderRadius: "28px",
  boxShadow:
    "0 25px 70px rgba(16,36,23,0.09), 0 8px 20px rgba(2,190,15,0.06)",
};

const headingStyle = {
  fontSize: "clamp(26px, 3vw, 36px)",
  lineHeight: 1.15,
  fontWeight: 800,
  color: DARK,
  margin: 0,
  letterSpacing: "-1px",
};

const paragraphStyle = {
  fontSize: "15px",
  lineHeight: 1.75,
  color: "#506257",
  margin: 0,
};

const SectionTitle = ({ eyebrow, title, children }) => (
  <div style={{ textAlign: "center", marginBottom: "48px" }}>
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 16px",
        borderRadius: "999px",
        background: LIGHT_GREEN,
        color: GREEN,
        fontSize: "13px",
        fontWeight: 800,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        marginBottom: "18px",
      }}
    >
      <span
        style={{
          width: "7px",
          height: "7px",
          borderRadius: "50%",
          background: GREEN,
          boxShadow: `0 0 0 5px rgba(2,190,15,0.10)`,
        }}
      />
      {eyebrow}
    </div>

    <h2 style={headingStyle}>{title}</h2>

    {children && (
      <div
        style={{
          maxWidth: "800px",
          margin: "18px auto 0",
        }}
      >
        {children}
      </div>
    )}
  </div>
);

const InfoCard = ({ icon, title, children }) => (
  <div
    style={{
      ...glassCard,
      padding: "30px",
      height: "100%",
      transition: "all 0.3s ease",
      position: "relative",
      overflow: "hidden",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-8px)";
      e.currentTarget.style.boxShadow =
        "0 35px 80px rgba(16,36,23,0.14), 0 12px 28px rgba(2,190,15,0.10)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow =
        "0 25px 70px rgba(16,36,23,0.09), 0 8px 20px rgba(2,190,15,0.06)";
    }}
  >
    <div
      style={{
        position: "absolute",
        width: "130px",
        height: "130px",
        borderRadius: "50%",
        background: "rgba(2,190,15,0.06)",
        right: "-45px",
        top: "-45px",
      }}
    />

    <div
      style={{
        width: "62px",
        height: "62px",
        borderRadius: "18px",
        background:
          "linear-gradient(145deg, #ffffff 0%, #eaffed 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "30px",
        boxShadow:
          "8px 8px 18px rgba(16,36,23,0.08), -5px -5px 12px rgba(255,255,255,0.9)",
        marginBottom: "22px",
        position: "relative",
        zIndex: 1,
      }}
    >
      {icon}
    </div>

    <h3
      style={{
        fontSize: "20px",
        margin: "0 0 12px",
        fontWeight: 800,
        color: DARK,
        position: "relative",
      }}
    >
      {title}
    </h3>

    <div style={{ position: "relative" }}>{children}</div>
  </div>
);

const JourneyCard = ({ number, title, children, last }) => (
  <div
    style={{
      flex: "1 1 220px",
      position: "relative",
      textAlign: "center",
    }}
  >
    <div
      style={{
        width: "76px",
        height: "76px",
        borderRadius: "24px",
        margin: "0 auto 22px",
        background: "linear-gradient(145deg, #ffffff, #dcffdf)",
        border: "1px solid rgba(2,190,15,0.15)",
        boxShadow:
          "10px 10px 25px rgba(16,36,23,0.10), -6px -6px 15px #ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: GREEN,
        fontSize: "27px",
        fontWeight: 900,
        position: "relative",
        zIndex: 2,
      }}
    >
      {number}
    </div>

    {!last && (
      <div
        style={{
          position: "absolute",
          top: "38px",
          left: "calc(50% + 38px)",
          width: "calc(100% - 76px)",
          height: "3px",
          background:
            "linear-gradient(90deg, rgba(2,190,15,0.45), rgba(2,190,15,0.08))",
        }}
      />
    )}

    <h3
      style={{
        margin: "0 0 10px",
        fontSize: "20px",
        fontWeight: 800,
      }}
    >
      {title}
    </h3>

    <p style={{ ...paragraphStyle, fontSize: "15px" }}>{children}</p>
  </div>
);

const LevelCard = ({ emoji, level, title, bg }) => (
  <div
    style={{
      padding: "25px 18px",
      borderRadius: "22px",
      background: bg,
      border: "1px solid rgba(16,36,23,0.06)",
      boxShadow: "0 15px 35px rgba(16,36,23,0.07)",
      textAlign: "center",
      transition: "transform 0.25s ease, box-shadow 0.25s ease",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-7px)";
      e.currentTarget.style.boxShadow =
        "0 25px 45px rgba(16,36,23,0.12)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow =
        "0 15px 35px rgba(16,36,23,0.07)";
    }}
  >
    <div style={{ fontSize: "30px", marginBottom: "12px" }}>{emoji}</div>

    <div
      style={{
        fontSize: "13px",
        fontWeight: 800,
        color: "#607066",
        marginBottom: "5px",
      }}
    >
      {level}
    </div>

    <div
      style={{
        fontSize: "17px",
        fontWeight: 900,
        color: DARK,
      }}
    >
      {title}
    </div>
  </div>
);

const ArteryVascularAge = () => {
  return (
    <div style={pageStyle}>
      {/* ================= HERO ================= */}
      <section
        style={{
          minHeight: "720px",
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          background:
            "radial-gradient(circle at 80% 20%, rgba(2,190,15,0.17), transparent 28%), linear-gradient(135deg, #f7fff8 0%, #ffffff 55%, #effff1 100%)",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "520px",
            height: "520px",
            borderRadius: "50%",
            right: "-150px",
            top: "-100px",
            border: "1px solid rgba(2,190,15,0.12)",
            boxShadow:
              "inset 0 0 80px rgba(2,190,15,0.05)",
          }}
        />

        <div
          style={{
            position: "absolute",
            width: "360px",
            height: "360px",
            borderRadius: "50%",
            right: "-40px",
            top: "20px",
            border: "1px solid rgba(2,190,15,0.10)",
          }}
        />

        <div style={containerStyle}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.05fr 0.95fr",
              gap: "70px",
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "9px",
                  padding: "10px 17px",
                  background: "#eaffed",
                  color: GREEN,
                  borderRadius: "999px",
                  fontWeight: 800,
                  fontSize: "13px",
                  marginBottom: "24px",
                }}
              >
                <span>❤️</span>
                ARTERY HEALTH SCREENING
              </div>

              {/* ONLY THIS MAIN HEADING IS MADE BIGGER */}
              <h1
                style={{
                  fontSize: "clamp(38px, 5vw, 58px)",
                  lineHeight: 1.08,
                  letterSpacing: "-2px",
                  fontWeight: 900,
                  margin: "0 0 24px",
                  color: DARK,
                }}
              >
                Vascular Age
                <br />
                <span style={{ color: GREEN }}>
                  & Artery Health Check
                </span>
              </h1>

              <p
                style={{
                  fontSize: "17px",
                  lineHeight: 1.5,
                  color: "#405248",
                  maxWidth: "650px",
                  margin: "0 0 25px",
                  fontWeight: 600,
                }}
              >
                Understand your vascular health. Take action earlier.
              </p>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                {["Non-Invasive", "Quick", "Painless"].map((item) => (
                  <div
                    key={item}
                    style={{
                      padding: "11px 18px",
                      background: "#ffffff",
                      borderRadius: "999px",
                      border: "1px solid rgba(2,190,15,0.14)",
                      boxShadow:
                        "0 8px 22px rgba(16,36,23,0.06)",
                      fontSize: "14px",
                      fontWeight: 800,
                      color: "#31503a",
                    }}
                  >
                    ✓ {item}
                  </div>
                ))}
              </div>
            </div>

            {/* 3D Visual */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: "390px",
                  height: "470px",
                  borderRadius: "190px 190px 60px 60px",
                  background:
                    "linear-gradient(145deg, #ffffff 0%, #eaffed 45%, #caffcf 100%)",
                  boxShadow:
                    "25px 35px 70px rgba(16,36,23,0.16), inset -12px -12px 30px rgba(2,190,15,0.08), inset 12px 12px 30px rgba(255,255,255,0.95)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  transform: "rotate(2deg)",
                }}
              >
                <div
                  style={{
                    width: "245px",
                    height: "245px",
                    borderRadius: "50%",
                    background:
                      "radial-gradient(circle, #ffffff 0%, #e9ffec 52%, #baffc1 100%)",
                    boxShadow:
                      "inset 0 0 45px rgba(2,190,15,0.12), 0 20px 40px rgba(16,36,23,0.10)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: "rotate(-2deg)",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "70px", lineHeight: 1 }}>
                      ❤️
                    </div>

                    <div
                      style={{
                        marginTop: "15px",
                        color: GREEN,
                        fontSize: "18px",
                        fontWeight: 900,
                      }}
                    >
                      VASCULAR
                    </div>

                    <div
                      style={{
                        color: DARK,
                        fontSize: "30px",
                        fontWeight: 900,
                      }}
                    >
                      HEALTH
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    position: "absolute",
                    right: "-25px",
                    top: "90px",
                    padding: "15px 19px",
                    borderRadius: "18px",
                    background: "#ffffff",
                    boxShadow:
                      "0 18px 40px rgba(16,36,23,0.14)",
                    fontWeight: 800,
                    color: GREEN,
                  }}
                >
                  🩸 Artery
                </div>

                <div
                  style={{
                    position: "absolute",
                    left: "-25px",
                    bottom: "100px",
                    padding: "15px 19px",
                    borderRadius: "18px",
                    background: "#ffffff",
                    boxShadow:
                      "0 18px 40px rgba(16,36,23,0.14)",
                    fontWeight: 800,
                    color: DARK,
                  }}
                >
                  📊 Screening
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= INTRO ================= */}
      <section style={sectionStyle}>
        <div style={containerStyle}>
          <div
            style={{
              ...glassCard,
              padding: "42px",
              background:
                "linear-gradient(135deg, #ffffff 0%, #f1fff3 100%)",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "22px",
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  minWidth: "64px",
                  width: "64px",
                  height: "64px",
                  borderRadius: "20px",
                  background: LIGHT_GREEN,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "31px",
                }}
              >
                🫀
              </div>

              <p style={{ ...paragraphStyle, fontSize: "16px" }}>
                Your arteries change with age, and factors such as high blood
                pressure, smoking, diabetes, cholesterol, excess weight and
                family history can affect cardiovascular health.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHAT IS VASCULAR AGE ================= */}
      <section style={{ ...sectionStyle, paddingTop: "20px" }}>
        <div style={containerStyle}>
          <SectionTitle
            eyebrow="Understanding Vascular Age"
            title="What is Vascular Age?"
          >
            <p style={paragraphStyle}>
              Vascular age is a way of communicating vascular health in
              relation to chronological age. Depending on the screening method
              used, measurements may provide information about arterial
              stiffness or cardiovascular risk.
            </p>
          </SectionTitle>

          <div
            style={{
              ...glassCard,
              padding: "38px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                width: "180px",
                height: "180px",
                borderRadius: "50%",
                background: "rgba(2,190,15,0.06)",
                right: "-50px",
                top: "-50px",
              }}
            />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "25px",
              }}
            >
              <div
                style={{
                  padding: "28px",
                  borderRadius: "22px",
                  background: "#f7fff8",
                  border: "1px solid rgba(2,190,15,0.10)",
                }}
              >
                <div style={{ fontSize: "35px", marginBottom: "15px" }}>
                  📈
                </div>

                <p style={paragraphStyle}>
                  A vascular-age result should be viewed as a wellness and
                  early-awareness indicator, not as a medical diagnosis.
                </p>
              </div>

              <div
                style={{
                  padding: "28px",
                  borderRadius: "22px",
                  background:
                    "linear-gradient(145deg, #ffffff, #ecffef)",
                  border: "1px solid rgba(2,190,15,0.10)",
                }}
              >
                <div style={{ fontSize: "35px", marginBottom: "15px" }}>
                  🩺
                </div>

                <p style={paragraphStyle}>
                  Arterial stiffness can be assessed using non-invasive
                  measures such as pulse-wave velocity, while confirmed
                  arterial narrowing/blockage may require clinical tests such
                  as ABI, Doppler/duplex ultrasound, or other investigations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHY CHECK ================= */}
      <section
        style={{
          ...sectionStyle,
          background:
            "linear-gradient(180deg, #f3fff5 0%, #ffffff 100%)",
        }}
      >
        <div style={containerStyle}>
          <SectionTitle
            eyebrow="Why It Matters"
            title="Why check your vascular health?"
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "22px",
            }}
          >
            <InfoCard
              icon="❤️"
              title="Understand your cardiovascular health"
            >
              <p style={paragraphStyle}>
                Understand your cardiovascular health
              </p>
            </InfoCard>

            <InfoCard
              icon="🩸"
              title="Learn more about arterial health"
            >
              <p style={paragraphStyle}>
                Learn more about arterial health
              </p>
            </InfoCard>

            <InfoCard
              icon="📊"
              title="Identify areas that may need attention"
            >
              <p style={paragraphStyle}>
                Identify areas that may need attention
              </p>
            </InfoCard>

            <InfoCard
              icon="🏃"
              title="Support healthier lifestyle decisions"
            >
              <p style={paragraphStyle}>
                Support healthier lifestyle decisions
              </p>
            </InfoCard>

            <InfoCard
              icon="🥗"
              title="Encourage better nutrition and physical activity"
            >
              <p style={paragraphStyle}>
                Encourage better nutrition and physical activity
              </p>
            </InfoCard>

            <InfoCard
              icon="🩺"
              title="Discuss concerning results with a healthcare professional"
            >
              <p style={paragraphStyle}>
                Discuss concerning results with a healthcare professional
              </p>
            </InfoCard>
          </div>
        </div>
      </section>

      {/* ================= AHA ================= */}
      <section style={sectionStyle}>
        <div style={containerStyle}>
          <div
            style={{
              ...glassCard,
              padding: "50px",
              background:
                "linear-gradient(135deg, #102417 0%, #183b21 100%)",
              color: "#ffffff",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                width: "350px",
                height: "350px",
                borderRadius: "50%",
                border: "1px solid rgba(255,255,255,0.08)",
                right: "-120px",
                top: "-130px",
              }}
            />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "0.8fr 1.2fr",
                gap: "45px",
                alignItems: "center",
                position: "relative",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "52px",
                    marginBottom: "18px",
                  }}
                >
                  ❤️
                </div>

                <h2
                  style={{
                    margin: 0,
                    fontSize: "clamp(26px, 3vw, 36px)",
                    lineHeight: 1.1,
                    fontWeight: 900,
                  }}
                >
                  Cardiovascular
                  <br />
                  Screening Factors
                </h2>
              </div>

              <p
                style={{
                  margin: 0,
                  fontSize: "16px",
                  lineHeight: 1.8,
                  color: "rgba(255,255,255,0.82)",
                }}
              >
                The American Heart Association notes that cardiovascular
                screening commonly considers factors including blood pressure,
                cholesterol, blood glucose, BMI, physical activity, smoking
                and sleep.
              </p>
            </div>

            <div
              style={{
                marginTop: "38px",
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              {[
                "Blood Pressure",
                "Cholesterol",
                "Blood Glucose",
                "BMI",
                "Physical Activity",
                "Smoking",
                "Sleep",
              ].map((item) => (
                <div
                  key={item}
                  style={{
                    padding: "12px 17px",
                    borderRadius: "999px",
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "#ffffff",
                    fontWeight: 700,
                    fontSize: "14px",
                  }}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHO MAY BENEFIT ================= */}
      <section
        style={{
          ...sectionStyle,
          background:
            "linear-gradient(180deg, #ffffff 0%, #f3fff5 100%)",
        }}
      >
        <div style={containerStyle}>
          <SectionTitle
            eyebrow="Preventive Health"
            title="Who may benefit?"
          >
            <p style={paragraphStyle}>
              The screening can be particularly useful as part of a broader
              preventive-health program for adults with cardiovascular risk
              factors or a family history of cardiovascular disease. NHS
              cardiovascular assessment tools, for example, incorporate
              factors such as age, smoking, blood pressure, cholesterol,
              diabetes and family history.
            </p>
          </SectionTitle>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(230px, 1fr))",
              gap: "20px",
            }}
          >
            {[
              ["🎂", "Age"],
              ["🚬", "Smoking"],
              ["🩸", "Blood pressure"],
              ["📊", "Cholesterol"],
              ["🩺", "Diabetes"],
              ["👨‍👩‍👧", "Family history"],
            ].map(([icon, title]) => (
              <div
                key={title}
                style={{
                  ...glassCard,
                  padding: "28px 20px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "34px",
                    marginBottom: "13px",
                  }}
                >
                  {icon}
                </div>

                <div
                  style={{
                    fontWeight: 800,
                    fontSize: "16px",
                    color: DARK,
                  }}
                >
                  {title}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= SCREENING JOURNEY ================= */}
      <section style={sectionStyle}>
        <div style={containerStyle}>
          <SectionTitle
            eyebrow="LifeOnPlus Experience"
            title="Your LifeOnPlus Screening Journey"
          />

          <div
            style={{
              ...glassCard,
              padding: "50px 35px",
              display: "flex",
              flexWrap: "wrap",
              gap: "25px",
              justifyContent: "space-between",
            }}
          >
            <JourneyCard number="1" title="Screening">
              A non-invasive vascular assessment is performed using the
              applicable screening technology.
            </JourneyCard>

            <JourneyCard number="2" title="Results">
              Your measurements are presented in an easy-to-understand report.
            </JourneyCard>

            <JourneyCard number="3" title="Understand">
              Review your vascular-health indicators and discuss concerns with
              a qualified healthcare professional when appropriate.
            </JourneyCard>

            <JourneyCard number="4" title="Take Action" last>
              Use the information to support healthier choices and appropriate
              follow-up.
            </JourneyCard>
          </div>
        </div>
      </section>

      {/* ================= SIX LEVEL ================= */}
      <section
        style={{
          ...sectionStyle,
          background:
            "linear-gradient(180deg, #f3fff5 0%, #ffffff 100%)",
        }}
      >
        <div style={containerStyle}>
          <SectionTitle
            eyebrow="Wellness Screening"
            title="Six-Level Wellness Screening"
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "16px",
            }}
          >
            <LevelCard
              emoji="🟢"
              level="Level 1"
              title="Optimal"
              bg="#edfff0"
            />

            <LevelCard
              emoji="🟢"
              level="Level 2"
              title="Good"
              bg="#f2fff4"
            />

            <LevelCard
              emoji="🟡"
              level="Level 3"
              title="Borderline"
              bg="#fffde8"
            />

            <LevelCard
              emoji="🟠"
              level="Level 4"
              title="Caution"
              bg="#fff4e8"
            />

            <LevelCard
              emoji="🔴"
              level="Level 5"
              title="High"
              bg="#fff0f0"
            />

            <LevelCard
              emoji="🔴"
              level="Level 6"
              title="Very High"
              bg="#ffeaea"
            />
          </div>

          <div
            style={{
              marginTop: "30px",
              padding: "24px 28px",
              borderRadius: "20px",
              background: "#ffffff",
              border: "1px solid rgba(2,190,15,0.12)",
              boxShadow:
                "0 12px 30px rgba(16,36,23,0.06)",
              display: "flex",
              gap: "16px",
              alignItems: "flex-start",
            }}
          >
            <div style={{ fontSize: "25px" }}>ℹ️</div>

            <p style={{ ...paragraphStyle, fontSize: "15px" }}>
              If LifeOnPlus uses these six levels, the exact thresholds should
              be defined by the underlying device/test protocol and validated
              before publishing them as clinical categories.
            </p>
          </div>
        </div>
      </section>

      {/* ================= IMPORTANT ================= */}
      <section style={sectionStyle}>
        <div style={containerStyle}>
          <div
            style={{
              borderRadius: "32px",
              padding: "50px",
              background:
                "linear-gradient(135deg, #fffdf2 0%, #ffffff 55%, #f7fff8 100%)",
              border: "1px solid rgba(240,170,0,0.18)",
              boxShadow:
                "0 25px 70px rgba(16,36,23,0.08)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                right: "-50px",
                top: "-50px",
                width: "180px",
                height: "180px",
                borderRadius: "50%",
                background: "rgba(255,193,7,0.08)",
              }}
            />

            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "22px",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: "66px",
                  height: "66px",
                  minWidth: "66px",
                  borderRadius: "21px",
                  background: "#fff4c7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "32px",
                  boxShadow:
                    "0 10px 25px rgba(160,120,0,0.10)",
                }}
              >
                ⚠️
              </div>

              <div>
                <h2
                  style={{
                    margin: "0 0 16px",
                    fontSize: "26px",
                    fontWeight: 900,
                    color: DARK,
                  }}
                >
                  Important
                </h2>

                <p
                  style={{
                    ...paragraphStyle,
                    fontSize: "15px",
                  }}
                >
                  This screening is intended for wellness and early awareness.
                  It does not replace medical diagnosis or professional medical
                  advice. A screening result suggesting elevated risk or an
                  abnormal finding should be discussed with an appropriate
                  healthcare professional. Confirmatory tests may be required
                  depending on the clinical situation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section
        style={{
          padding: "40px 0 90px",
        }}
      >
        <div style={containerStyle}>
          <div
            style={{
              borderRadius: "35px",
              padding: "55px 35px",
              textAlign: "center",
              background:
                "linear-gradient(135deg, #02BE0F 0%, #35A93E 50%, #087d15 100%)",
              color: "#ffffff",
              boxShadow:
                "0 30px 80px rgba(2,190,15,0.25)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                width: "300px",
                height: "300px",
                borderRadius: "50%",
                border: "1px solid rgba(255,255,255,0.15)",
                left: "-120px",
                top: "-120px",
              }}
            />

            <div
              style={{
                position: "relative",
              }}
            >
              <div
                style={{
                  fontSize: "50px",
                  marginBottom: "15px",
                }}
              >
                ❤️
              </div>

              <h2
                style={{
                  margin: "0 0 14px",
                  fontSize: "clamp(26px, 3vw, 36px)",
                  fontWeight: 900,
                }}
              >
                Understand your vascular health.
              </h2>

              <p
                style={{
                  margin: 0,
                  color: "rgba(255,255,255,0.88)",
                  fontSize: "16px",
                  lineHeight: 1.6,
                }}
              >
                Take action earlier.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= RESPONSIVE CSS ================= */}
      <style>
        {`
          @media (max-width: 900px) {
            .artery-page {
              padding: 0;
            }
          }

          @media (max-width: 800px) {
            div[style*="grid-template-columns: 1.05fr 0.95fr"] {
              grid-template-columns: 1fr !important;
            }

            div[style*="grid-template-columns: 0.8fr 1.2fr"] {
              grid-template-columns: 1fr !important;
            }

            div[style*="grid-template-columns: 1fr 1fr"] {
              grid-template-columns: 1fr !important;
            }
          }

          @media (max-width: 600px) {
            section {
              padding-left: 0 !important;
              padding-right: 0 !important;
            }

            h1 {
              font-size: 38px !important;
              letter-spacing: -1.5px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ArteryVascularAge;